import classNames from 'classnames';
import { uniqBy } from 'lodash';
import CSSMotion from 'rc-motion';
import { render } from 'rc-util/lib/React/render';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import getClsWithPrefix from '../cls';
import './index.less';

const subPrefix = 'Message';
const cls$ = getClsWithPrefix(subPrefix);
export const MESSAGE_LIST_CLASS_NAME = cls$('list');

interface MessageProps {
    duration?: number;
    icon?: React.ReactNode;
    content: React.ReactNode;
}

const defaultDuration = 3;

const MessageUI: React.FC<MessageProps> = ({ duration = defaultDuration, icon, content }) => {
    const [open, setOpen] = useState(true);

    useEffect(() => {
        if (duration === 0) return;
        const timer = setTimeout(() => {
            setOpen(false);
        }, duration * 1000);
        return () => clearTimeout(timer);
    }, [duration]);

    const edgeCss: React.CSSProperties = {
        padding: 0,
        height: 0,
        opacity: 0,
    };

    return (
        <CSSMotion
            motionName={cls$('move')}
            visible={open}
            onAppearStart={() => edgeCss}
            onEnterStart={() => edgeCss}
            onLeaveActive={() => edgeCss}
        >
            {({ style, className: motionClassName }) => (
                <div className={classNames(cls$(), motionClassName)} style={style}>
                    <div className={cls$('inner')}>
                        {!!icon && <div className={cls$('icon')}>{icon}</div>}
                        <div className={cls$('content')}>{content}</div>
                    </div>
                </div>
            )}
        </CSSMotion>
    );
};

const MessageListWrapper = React.forwardRef<{ open: (cfg: MessageConfig) => void }, {}>((_, ref) => {
    type MessageList = { key: string; cfg: Omit<MessageConfig, 'key'> }[];
    const [messageList, setMessageList] = useState<MessageList>([]);

    useImperativeHandle(
        ref,
        () => ({
            open: ({ key = `${Date.now()}`, ...cfg }) => {
                setMessageList(uniqBy([...messageList, { key, cfg }], 'key'));
            },
        }),
        [setMessageList],
    );

    if (!messageList.length) return <></>;

    return (
        <div className={MESSAGE_LIST_CLASS_NAME}>
            {messageList.map(({ key, cfg: { icon, content, duration = defaultDuration } }) => (
                <MessageUI key={key} icon={icon} content={content} duration={duration} />
            ))}
        </div>
    );
});

interface MessageConfig {
    key?: string;
    duration?: number;
    icon?: React.ReactNode;
    content?: React.ReactNode;
}

interface MessageInstance {
    config(config: MessageConfig): void;
    success(config: MessageConfig | string): void;
    info(config: MessageConfig | string): void;
    error(config: MessageConfig | string): void;
}

let messageWrapperEl: HTMLDivElement;

const commonMessage = (cfg: MessageConfig) => {
    if (!messageWrapperEl || !document.contains(messageWrapperEl)) {
        messageWrapperEl = document.createElement('div');
        document.body.appendChild(messageWrapperEl);
    }

    render(<MessageListWrapper ref={(ref) => ref?.open(cfg)} />, messageWrapperEl);
};

const getSuccessLikeFn: (params: { defaultIconName: string; defaultIconColor: string }) => MessageInstance['success'] =
    ({ defaultIconName, defaultIconColor }) =>
    (config) => {
        const defaultIcon = <IconFont name={defaultIconName} fontSize={20} color={defaultIconColor} />;
        commonMessage(
            typeof config === 'string'
                ? {
                      icon: defaultIcon,
                      content: config,
                  }
                : {
                      icon: defaultIcon,
                      ...config,
                  },
        );
    };

const message: MessageInstance = {
    config: commonMessage,
    success: getSuccessLikeFn({
        defaultIconName: 'CheckCircleFillBold',
        defaultIconColor: 'var(--content-system-info-icon-success, #4FC62B)',
    }),
    info: getSuccessLikeFn({
        defaultIconName: 'InfoCircleBoldFill',
        defaultIconColor: 'var(--content-system-info-icon-regular, #8CC0FF)',
    }),
    error: getSuccessLikeFn({
        defaultIconName: 'ExclamationMarkCircleBoldFill',
        defaultIconColor: 'var(--content-system-info-icon-warning, #F74640)',
    }),
};

export default message;
